import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAircraftTypeAutocomplete, useStarredAircraftTypes } from '../../../hooks';
import type { AircraftTypeAutocomplete as AircraftTypeAutocompleteType } from '../../../services/redux/api/aircraftType.api';
import { useLazyAutocompleteAircraftTypesQuery } from '../../../services/redux/api/aircraftType.api';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

interface AircraftTypeAutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSelect?: (aircraftType: AircraftTypeAutocompleteType) => void;
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  onBlur?: (value: string) => void;
}

/** Format the enriched display string shown in the input after selection */
function formatDisplay(acType: AircraftTypeAutocompleteType): string {
  if (acType.manufacturer) {
    return `${acType.aircraftType} — ${acType.manufacturer} ${acType.model}`;
  }
  return acType.aircraftType;
}

export function AircraftTypeAutocomplete({
  label,
  value,
  onChange,
  onSelect,
  placeholder,
  required,
  error,
  onBlur,
}: AircraftTypeAutocompleteProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [codeValue, setCodeValue] = useState(value);
  const [displayValue, setDisplayValue] = useState(value);
  const [isShowingDetail, setIsShowingDetail] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { aircraftTypes: hookAircraftTypes, loading, search } = useAircraftTypeAutocomplete({ delay: 300, limit: 10 });
  const allAircraftTypes = Array.isArray(hookAircraftTypes) ? hookAircraftTypes : [];
  const aircraftTypes = allAircraftTypes.filter(a =>
    a.aircraftType.startsWith(codeValue.toUpperCase())
  );

  const { starredTypes, isStarred: isAircraftStarred, toggle: toggleAircraftStar } = useStarredAircraftTypes();

  // Persistent cache of aircraft type metadata accumulated across searches + favorite lookups
  const seenTypesRef = useRef<Map<string, AircraftTypeAutocompleteType>>(new Map());
  allAircraftTypes.forEach(a => seenTypesRef.current.set(a.aircraftType, a));

  // Lazy query to fetch metadata for starred types not yet in cache
  const [fetchType] = useLazyAutocompleteAircraftTypesQuery();
  const pendingLookupsRef = useRef<Set<string>>(new Set());

  // Proactively fetch metadata for any starred code we haven't seen yet
  useEffect(() => {
    starredTypes.forEach(code => {
      if (!seenTypesRef.current.has(code) && !pendingLookupsRef.current.has(code)) {
        pendingLookupsRef.current.add(code);
        fetchType({ query: code, limit: 5 })
          .unwrap()
          .then(results => {
            results.forEach(a => seenTypesRef.current.set(a.aircraftType, a));
            pendingLookupsRef.current.delete(code);
            // Trigger re-render so favoriteItems picks up the new data
            setFavVersion(v => v + 1);
          })
          .catch(() => {
            pendingLookupsRef.current.delete(code);
          });
      }
    });
  }, [starredTypes, fetchType]);

  // Counter to force favoriteItems recalculation after async lookups resolve
  const [favVersion, setFavVersion] = useState(0);

  // Resolve starred codes to full objects using the accumulated cache
  const favoriteItems = useMemo(() => {
    return Array.from(starredTypes).map(code => {
      return seenTypesRef.current.get(code)
        || { aircraftType: code, aircraftGroup: '', manufacturer: '', model: '', variant: '', icaoCode: '' } as AircraftTypeAutocompleteType;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [starredTypes, allAircraftTypes, favVersion]);

  // Which mode the dropdown is in: favorites-only (empty input) or search results
  const isSearchMode = codeValue.length >= 1;
  // The list currently navigable by keyboard
  const activeItems = isSearchMode ? aircraftTypes : favoriteItems;

  const hasError = error && error.trim() !== '';

  // Sync from parent value prop
  useEffect(() => {
    setCodeValue(value);
    if (!isShowingDetail) {
      setDisplayValue(value);
    }
    if (value !== codeValue) {
      setDisplayValue(value);
      setIsShowingDetail(false);
    }
  }, [value]);

  // Auto-resolve enriched display on mount / when data loads (only when not focused)
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    if (codeValue && !isShowingDetail && allAircraftTypes.length > 0) {
      const match = allAircraftTypes.find(a => a.aircraftType === codeValue);
      if (match) {
        setDisplayValue(formatDisplay(match));
        setIsShowingDetail(true);
      }
    }
  }, [allAircraftTypes, codeValue]);

  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const updatePosition = () => {
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();

      const handleScroll = (e: Event) => {
        if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
          return;
        }
        setShowDropdown(false);
      };

      const handleResize = () => {
        setShowDropdown(false);
      };

      const handleClickOutside = (e: MouseEvent) => {
        if (
          inputRef.current && !inputRef.current.contains(e.target as Node) &&
          dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
        ) {
          setShowDropdown(false);
        }
      };

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
    setCodeValue(raw);
    setDisplayValue(raw);
    setIsShowingDetail(false);
    onChange(raw);

    if (raw.length >= 1) {
      search(raw);
      setShowDropdown(true);
      setSelectedIndex(-1);
    } else {
      // Empty input — show favorites if any, otherwise close
      setShowDropdown(starredTypes.size > 0);
      setSelectedIndex(-1);
    }
  };

  const handleSelectAircraftType = (aircraftType: AircraftTypeAutocompleteType) => {
    setCodeValue(aircraftType.aircraftType);
    setDisplayValue(formatDisplay(aircraftType));
    setIsShowingDetail(true);
    onChange(aircraftType.aircraftType);
    onSelect?.(aircraftType);
    setShowDropdown(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowDropdown(false);
      setSelectedIndex(-1);
      onBlur?.(codeValue);
    }, 200);
  };

  const handleFocus = () => {
    if (isShowingDetail) {
      setDisplayValue(codeValue);
      setIsShowingDetail(false);
      if (codeValue.length >= 1) {
        search(codeValue);
        setShowDropdown(true);
      } else if (starredTypes.size > 0) {
        setShowDropdown(true);
      }
    } else if (codeValue.length >= 1 && aircraftTypes.length > 0) {
      setShowDropdown(true);
    } else if (starredTypes.size > 0) {
      setShowDropdown(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || activeItems.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, activeItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < activeItems.length) {
          handleSelectAircraftType(activeItems[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const renderDropdown = () => {
    if (!showDropdown) return null;
    // Need either favorites (empty input) or search results (typing)
    if (!isSearchMode && favoriteItems.length === 0) return null;

    const dropdown = (
      <div
        ref={dropdownRef}
        onMouseDown={(e) => e.preventDefault()}
        style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          maxHeight: '240px',
          overflowY: 'auto',
          overflowX: 'hidden',
          zIndex: 9999,
        }}
        className="bg-white border border-gray-200 rounded-lg shadow-xl"
      >
        {/* Favorites mode — empty input */}
        {!isSearchMode && favoriteItems.length > 0 && (
          <>
            <div className="px-3 pt-2 pb-1 flex items-center gap-1.5">
              <StarIcon sx={{ fontSize: 12 }} className="text-amber-400" />
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Favorites</span>
            </div>
            <ul className="py-0.5 pb-1">
              {favoriteItems.map((acType, index) => (
                <li key={acType.aircraftType}>
                  <button
                    type="button"
                    onClick={() => handleSelectAircraftType(acType)}
                    className={`w-full px-3 py-2 text-left transition-colors
                      ${index === selectedIndex
                        ? 'bg-primary-50'
                        : 'hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold text-sm font-mono ${index === selectedIndex ? 'text-primary-700' : 'text-gray-900'}`}>
                          {acType.aircraftType}
                        </span>
                        {acType.manufacturer && (
                          <span className={`text-sm ${index === selectedIndex ? 'text-primary-500' : 'text-gray-500'}`}>
                            — {acType.manufacturer} {acType.model}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleAircraftStar(acType.aircraftType);
                        }}
                        className="p-0 border-0 bg-transparent cursor-pointer flex items-center"
                      >
                        <StarIcon sx={{ fontSize: 14 }} className="text-amber-400" />
                      </button>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Search mode — typing */}
        {isSearchMode && (
          <>
            {loading && (
              <div className="px-3 py-2.5 text-xs text-gray-400">
                Searching aircraft types...
              </div>
            )}

            {!loading && aircraftTypes.length === 0 && (
              <div className="px-3 py-2.5 text-xs text-gray-400">
                No aircraft types found
              </div>
            )}

            {!loading && aircraftTypes.length > 0 && (
              <ul className="py-1">
                {aircraftTypes.map((acType, index) => (
                  <li key={acType.aircraftType}>
                    <button
                      type="button"
                      onClick={() => handleSelectAircraftType(acType)}
                      className={`w-full px-3 py-2 text-left transition-colors
                        ${index === selectedIndex
                          ? 'bg-primary-50'
                          : 'hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold text-sm font-mono ${index === selectedIndex ? 'text-primary-700' : 'text-gray-900'}`}>
                            {acType.aircraftType}
                          </span>
                          <span className={`text-sm ${index === selectedIndex ? 'text-primary-500' : 'text-gray-500'}`}>
                            — {acType.manufacturer} {acType.model}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAircraftStar(acType.aircraftType);
                          }}
                          className="p-0 border-0 bg-transparent cursor-pointer flex items-center"
                        >
                          {isAircraftStarred(acType.aircraftType) ? (
                            <StarIcon sx={{ fontSize: 14 }} className="text-amber-400" />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 14 }} className="text-gray-400 hover:text-amber-300" />
                          )}
                        </button>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    );

    return createPortal(dropdown, document.body);
  };

  return (
    <label className="block mb-2">
      <span className="block mb-1.5 text-base font-semibold text-gray-600">
        {label}
        {required && <span className="text-danger-500 ml-1">*</span>}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoCapitalize="characters"
        className={`w-full px-3 py-2.5 bg-white border rounded-lg text-gray-900 text-lg outline-none transition-all
          ${isShowingDetail ? 'text-base' : ''}
          ${hasError
            ? 'border-danger-500 focus:border-danger-500 focus:ring-3 focus:ring-danger-500/10'
            : 'border-gray-300 focus:border-primary-500 focus:ring-3 focus:ring-primary-500/10'
          }`}
      />

      {renderDropdown()}

    </label>
  );
}
